
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';


import { usePermissions } from '@/contexts/unified-permissions';

/**
 * A simplified hook for retrieving a user's referral code
 * with better caching and performance
 */
export function useReferralCodeSimple() {
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, user } = usePermissions();

  // Use sessionStorage to cache referral code
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Check storage cache first
    const cachedCode = sessionStorage.getItem(`referral_code_${user.id}`);
    
    if (cachedCode) {
      setReferralCode(cachedCode);
      return;
    }

    // Fetch only if needed
    const fetchReferralCode = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const { data, error } = await supabase
          .from('referral_codes')
          .select('code')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching referral code:', error);
          setError(error.message);
        } else if (data?.code) {
          setReferralCode(data.code);
          // Cache the code
          sessionStorage.setItem(`referral_code_${user.id}`, data.code);
        }
      } catch (error: unknown) {
        console.error('Error in fetchReferralCode:', error);
        setError(error instanceof Error ? error.message : String(error));
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferralCode();
  }, [isAuthenticated, user]);

  return {
    referralCode,
    isLoading,
    error
  };
}
